import { useCallback, useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import mm from './MegaNav.module.scss'

export interface MegaNavItem {
  id: number | string
  img: string
  title: string
  description?: string
  path: string
}

export interface MegaMenu {
  key: string
  label: string
  items: MegaNavItem[]
  columns: number
  footer?: { label: string; cta: string; href: string }
}

export interface MegaLink {
  label: string
  href: string
  icon?: string
  external?: boolean
}

export type MegaEntry =
  | { type: 'link'; link: MegaLink }
  | { type: 'menu'; menu: MegaMenu }

interface MegaNavProps {
  entries: MegaEntry[]
}

const CLOSE_DELAY = 140
const EDGE_PADDING = 16

const Chevron = ({ open }: { open: boolean }) => (
  <svg
    className={`${mm.chevron} ${open ? mm.chevronOpen : ''}`}
    viewBox='0 0 10 10'
    fill='none'
    aria-hidden='true'
  >
    <path
      d='M2 3.5 5 6.5 8 3.5'
      stroke='currentColor'
      strokeWidth='1.4'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

const MegaNav = ({ entries }: MegaNavProps) => {
  const menus = entries
    .filter((e): e is { type: 'menu'; menu: MegaMenu } => e.type === 'menu')
    .map((e) => e.menu)

  const triggerRefs = useRef<Record<string, HTMLButtonElement | null>>({})
  const contentRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastKey = useRef<string>(menus[0]?.key ?? '')

  const [active, setActive] = useState<string | null>(null)
  const [sizes, setSizes] = useState<Record<string, { w: number; h: number }>>(
    {}
  )
  const [left, setLeft] = useState(0)

  // Measure each menu's natural size so the shared panel can morph between them.
  const measure = useCallback(() => {
    setSizes((prev) => {
      const next: Record<string, { w: number; h: number }> = { ...prev }
      for (const menu of menus) {
        const el = contentRefs.current[menu.key]
        if (el) next[menu.key] = { w: el.offsetWidth, h: el.offsetHeight }
      }
      return next
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menus.length])

  useEffect(() => {
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [measure])

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }, [])

  const scheduleClose = useCallback(() => {
    cancelClose()
    closeTimer.current = setTimeout(() => setActive(null), CLOSE_DELAY)
  }, [cancelClose])

  const openMenu = useCallback(
    (key: string) => {
      cancelClose()
      lastKey.current = key
      const btn = triggerRefs.current[key]
      const width = sizes[key]?.w ?? 0
      if (btn) {
        const parent = btn.offsetParent as HTMLElement | null
        const center = btn.offsetLeft + btn.offsetWidth / 2
        let nextLeft = center - width / 2
        if (parent) {
          const max = parent.clientWidth - width - EDGE_PADDING
          nextLeft = Math.min(Math.max(nextLeft, EDGE_PADDING), Math.max(EDGE_PADDING, max))
        }
        setLeft(nextLeft)
      }
      setActive(key)
    },
    [cancelClose, sizes]
  )

  const displayKey = active ?? lastKey.current
  const size = sizes[displayKey]

  return (
    <>
      <div className={mm.row} onMouseLeave={scheduleClose} onMouseEnter={cancelClose}>
        {entries.map((entry, i) => {
          if (entry.type === 'link') {
            const { link } = entry
            return (
              <Link
                key={`link-${i}`}
                href={link.href}
                className={mm.link}
                onMouseEnter={() => setActive(null)}
                {...(link.external && {
                  target: '_blank',
                  rel: 'noopener noreferrer',
                })}
              >
                {link.label}
                {link.icon && (
                  <span className={mm.iconWrapper}>
                    <Image src={link.icon} alt='' width={12} height={12} />
                  </span>
                )}
              </Link>
            )
          }

          const { menu } = entry
          const isOpen = active === menu.key
          return (
            <button
              key={menu.key}
              type='button'
              ref={(el) => {
                triggerRefs.current[menu.key] = el
              }}
              className={`${mm.trigger} ${isOpen ? mm.triggerOpen : ''}`}
              onMouseEnter={() => openMenu(menu.key)}
              onClick={() => openMenu(menu.key)}
              aria-expanded={isOpen}
            >
              {menu.label}
              <Chevron open={isOpen} />
            </button>
          )
        })}
      </div>

      <div
        className={mm.panelWrap}
        style={{
          left,
          opacity: active ? 1 : 0,
          transform: active
            ? 'translateY(0) scale(1)'
            : 'translateY(-4px) scale(0.98)',
          pointerEvents: active ? 'auto' : 'none',
        }}
        onMouseEnter={cancelClose}
        onMouseLeave={scheduleClose}
      >
        <div
          className={mm.panel}
          style={{ width: size?.w, height: size?.h }}
        >
          {menus.map((menu) => (
            <div
              key={menu.key}
              ref={(el) => {
                contentRefs.current[menu.key] = el
              }}
              className={`${mm.content} ${
                active === menu.key ? mm.contentActive : ''
              }`}
            >
              <div
                className={mm.grid}
                style={{
                  gridTemplateColumns: `repeat(${menu.columns}, max-content)`,
                }}
              >
                {menu.items.map((item) => (
                  <Link
                    key={item.id}
                    href={item.path}
                    className={mm.item}
                    onClick={() => setActive(null)}
                    {...(item.path.startsWith('http') && {
                      target: '_blank',
                      rel: 'noopener noreferrer',
                    })}
                  >
                    <span className={mm.itemIcon}>
                      <Image src={item.img} alt='' width={22} height={18} />
                    </span>
                    <span className={mm.itemText}>
                      <span className={mm.itemTitle}>{item.title}</span>
                      {item.description && (
                        <span className={mm.itemDesc}>{item.description}</span>
                      )}
                    </span>
                  </Link>
                ))}
              </div>

              {menu.footer && (
                <div className={mm.footer}>
                  <span className={mm.footerLabel}>{menu.footer.label}</span>
                  <Link href={menu.footer.href} className={mm.footerLink}>
                    {menu.footer.cta}
                    <span aria-hidden='true'>→</span>
                  </Link>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

export default MegaNav
